import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { TaskListTemplateItemModel } from '@domain-group/realhub-backend-api-types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/taskListTemplateItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/taskListTemplateItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/taskListTemplateItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/taskListTemplateItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/taskListTemplateItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/taskListTemplateItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/taskListTemplateItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/taskListTemplateItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/taskListTemplateItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/taskListTemplateItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/taskListTemplateItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/taskListTemplateItem/DELETE_FAILURE'

export type TaskListTemplateItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TaskListTemplateItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTaskListTemplateItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().taskListTemplateItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/task_list_template_items.json', options)
      .then(({ data }: { data: TaskListTemplateItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTaskListTemplateItem(taskListTemplateItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.taskListTemplateItems.loadedIds]
    const loadedForKeys = [...state.taskListTemplateItems.loadedForKeys]

    if (taskListTemplateItemId && !loadedIds.includes(taskListTemplateItemId)){
      loadedIds.push(taskListTemplateItemId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/task_list_template_items/${taskListTemplateItemId}.json`, options)
      .then(({ data }: { data: TaskListTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createTaskListTemplateItem(taskListTemplateItem: Partial<TaskListTemplateItemModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      task_list_template_item: taskListTemplateItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/task_list_template_items.json', options, config)
      .then(({ data }: { data: TaskListTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateTaskListTemplateItem(
  taskListTemplateItem: UpdateParams<TaskListTemplateItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      task_list_template_item: taskListTemplateItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/task_list_template_items/${taskListTemplateItem.id}.json`, options, config)
      .then(({ data }: { data: TaskListTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteTaskListTemplateItem(taskListTemplateItem: DeleteParams<TaskListTemplateItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/task_list_template_items/${taskListTemplateItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TaskListTemplateItemModuleState = initialState,
  action: Action = { type: '' },
): TaskListTemplateItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
